import { lazy, Suspense, type ComponentType, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, Outlet, useLocation, useRouteError, isRouteErrorResponse, Link } from 'react-router'
import { useAuth, usePerms } from '@/lib/auth'
import { AppShell } from '@/components/layout/AppShell'
import { Button, EmptyState, Spinner } from '@/components/ui'
import { ShieldOff } from 'lucide-react'

function page(loader: () => Promise<{ default: ComponentType }>) {
  const C = lazy(loader)
  return (
    <Suspense
      fallback={
        <div className="flex h-[50vh] items-center justify-center">
          <Spinner />
        </div>
      }
    >
      <C />
    </Suspense>
  )
}

function RequireAuth() {
  const { status, platform } = useAuth()
  const loc = useLocation()
  if (status === 'loading')
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner />
      </div>
    )
  if (status === 'anonymous') return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />
  // Platform administrators have no hospital: keep them in the product console.
  if (platform && !loc.pathname.startsWith('/console')) return <Navigate to="/console" replace />
  return <Outlet />
}

/** Product console: PerfexData platform administrators only. */
function RequirePlatform() {
  const { platform } = useAuth()
  if (!platform) return <Navigate to="/app" replace />
  return <Outlet />
}

/** UI guard only — the API enforces the same permission on every request. */
function Guard({ any, module, children }: { any?: string[]; module?: string; children: ReactNode }) {
  const { canAny, hasModule } = usePerms()
  if ((any && !canAny(...any)) || (module && !hasModule(module))) {
    return (
      <EmptyState
        icon={<ShieldOff className="h-5 w-5" />}
        title="You don't have access to this area"
        description="Ask your hospital administrator if you need this permission. Attempts to reach restricted areas are recorded."
        action={
          <Link to="/app">
            <Button variant="secondary">Back to dashboard</Button>
          </Link>
        }
      />
    )
  }
  return <>{children}</>
}

function RouteError() {
  const err = useRouteError()
  // No error at all: rendered by a catch-all (`*`) route, i.e. an unknown address.
  const notFound = err == null || (isRouteErrorResponse(err) && err.status === 404)
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center p-6 text-center">
      <p className="font-display text-5xl font-bold text-brand-700">{notFound ? '404' : 'Oops'}</p>
      <p className="mt-3 text-lg font-semibold text-ink-900">{notFound ? 'Page not found' : 'Something went wrong'}</p>
      <p className="mt-1 max-w-md text-sm text-ink-500">
        {notFound ? 'The page you are looking for does not exist or has moved.' : 'An unexpected error occurred. Reload the page to continue.'}
      </p>
      <div className="mt-6 flex gap-2">
        <Link to="/">
          <Button variant="secondary">Home</Button>
        </Link>
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </div>
    </div>
  )
}

const g = (any: string[] | undefined, module: string | undefined, el: ReactNode) => (
  <Guard any={any} module={module}>
    {el}
  </Guard>
)

export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      // Product website
      {
        element: page(() => import('@/site/SiteLayout')),
        children: [
          { index: true, element: page(() => import('@/site/LandingPage')) },
          { path: 'features', element: page(() => import('@/site/FeaturesPage')) },
          { path: 'pricing', element: page(() => import('@/site/PricingPage')) },
          { path: 'about', element: page(() => import('@/site/AboutPage')) },
          { path: 'contact', element: page(() => import('@/site/ContactPage')) },
          { path: 'security', element: page(() => import('@/site/SecurityPage')) },
        ],
      },
      { path: 'login', element: page(() => import('@/pages/auth/LoginPage')) },
      { path: 'forgot-password', element: page(() => import('@/pages/auth/ForgotPasswordPage')) },
      { path: 'reset-password', element: page(() => import('@/pages/auth/ResetPasswordPage')) },

      // Patient portal
      { path: 'portal/login', element: page(() => import('@/portal/PortalLogin')) },
      { path: 'portal/activate', element: page(() => import('@/portal/PortalActivate')) },
      { path: 'portal', element: page(() => import('@/portal/PortalHome')) },
      { path: 'portal/print/lab/:orderId', element: page(() => import('@/portal/PortalLabReportPrint')) },

      // Staff application
      {
        element: <RequireAuth />,
        children: [
          { path: 'app/queue/display/:deptId', element: page(() => import('@/pages/queue/QueueDisplayPage')) },
          { path: 'print/invoice/:id', element: page(() => import('@/print/InvoicePrint')) },
          { path: 'print/lab/:orderId', element: page(() => import('@/print/LabReportPrint')) },
          { path: 'print/prescription/:id', element: page(() => import('@/print/PrescriptionPrint')) },
          { path: 'print/discharge/:id', element: page(() => import('@/print/DischargePrint')) },
          { path: 'print/discharge-slip/:id', element: page(() => import('@/print/DischargeSlip')) },
          {
            path: 'console',
            element: <RequirePlatform />,
            children: [
              {
                element: page(() => import('@/console/ConsoleShell')),
                children: [
                  { index: true, element: page(() => import('@/console/OverviewPage')) },
                  { path: 'customers', element: page(() => import('@/console/CustomersPage')) },
                  { path: 'customers/new', element: page(() => import('@/console/NewCustomerPage')) },
                  { path: 'customers/:id', element: page(() => import('@/console/CustomerDetailPage')) },
                  { path: 'leads', element: page(() => import('@/console/LeadsPage')) },
                  { path: 'team', element: page(() => import('@/console/TeamPage')) },
                  { path: 'account', element: page(() => import('@/console/AccountPage')) },
                  { path: 'resources', element: page(() => import('@/docs/ResourcesPage').then((m) => ({ default: m.ConsoleResources }))) },
                ],
              },
            ],
          },
          {
            path: 'app',
            element: <AppShell />,
            errorElement: <RouteError />,
            children: [
              { index: true, element: page(() => import('@/pages/dashboard/DashboardPage')) },
              { path: 'analytics', element: g(['analytics.executive'], undefined, page(() => import('@/pages/analytics/AnalyticsPage'))) },
              { path: 'approvals', element: page(() => import('@/pages/approvals/ApprovalsPage')) },
              { path: 'patients', element: g(['patients.read'], undefined, page(() => import('@/pages/patients/PatientsPage'))) },
              { path: 'offline', element: g(['patients.write', 'appointments.manage'], undefined, page(() => import('@/pages/offline/OfflinePage'))) },
              { path: 'patients/new', element: g(['patients.write'], undefined, page(() => import('@/pages/patients/RegisterPatientPage'))) },
              { path: 'patients/:id', element: g(['patients.read'], undefined, page(() => import('@/pages/patients/PatientProfilePage'))) },
              { path: 'appointments', element: g(['appointments.read'], 'appointments', page(() => import('@/pages/appointments/AppointmentsPage'))) },
              { path: 'appointments/schedules', element: g(['appointments.read'], 'appointments', page(() => import('@/pages/appointments/SchedulesPage'))) },
              { path: 'queue', element: g(['appointments.read', 'clinical.write'], 'appointments', page(() => import('@/pages/queue/QueuePage'))) },
              { path: 'opd', element: g(['clinical.read'], 'opd', page(() => import('@/pages/opd/OpdWorklistPage'))) },
              { path: 'referrals', element: g(['clinical.read'], 'opd', page(() => import('@/pages/opd/ReferralsPage'))) },
              { path: 'encounters/:id', element: g(['clinical.read'], undefined, page(() => import('@/pages/opd/EncounterWorkspace'))) },
              { path: 'ipd', element: g(['ipd.read'], 'ipd', page(() => import('@/pages/ipd/IpdPage'))) },
              { path: 'ipd/admissions/:id', element: g(['ipd.read'], 'ipd', page(() => import('@/pages/ipd/AdmissionPage'))) },
              { path: 'nursing', element: g(['nursing.write'], 'nursing', page(() => import('@/pages/ipd/NursingStationPage'))) },
              { path: 'lab', element: g(['lab.read'], 'lab', page(() => import('@/pages/lab/LabPage'))) },
              { path: 'radiology', element: g(['radiology.read'], 'radiology', page(() => import('@/pages/radiology/RadiologyPage'))) },
              { path: 'pharmacy', element: g(['pharmacy.read'], 'pharmacy', page(() => import('@/pages/pharmacy/PharmacyPage'))) },
              { path: 'procurement', element: g(['procurement.read'], 'procurement', page(() => import('@/pages/procurement/ProcurementPage'))) },
              { path: 'billing', element: g(['billing.read'], undefined, page(() => import('@/pages/billing/BillingPage'))) },
              { path: 'billing/invoices/:id', element: g(['billing.read'], undefined, page(() => import('@/pages/billing/InvoicePage'))) },
              { path: 'insurance', element: g(['insurance.read'], 'insurance', page(() => import('@/pages/insurance/InsurancePage'))) },
              { path: 'hr', element: g(['hr.read'], 'hr', page(() => import('@/pages/hr/HrPage'))) },
              { path: 'reports', element: g(['reports.view'], undefined, page(() => import('@/pages/reports/ReportsPage'))) },
              { path: 'admin/users', element: g(['admin.users', 'admin.roles'], undefined, page(() => import('@/pages/admin/UsersPage'))) },
              {
                path: 'admin/facilities',
                element: g(['admin.facilities', 'admin.catalog', 'beds.manage', 'lab.catalog', 'pharmacy.catalog'], undefined, page(() => import('@/pages/admin/FacilitiesPage'))),
              },
              { path: 'admin/settings', element: g(['admin.settings'], undefined, page(() => import('@/pages/admin/SettingsPage'))) },
              { path: 'admin/audit', element: g(['audit.view'], undefined, page(() => import('@/pages/admin/AuditPage'))) },
              { path: 'profile', element: page(() => import('@/pages/profile/ProfilePage')) },
              { path: 'resources', element: page(() => import('@/docs/ResourcesPage')) },
              { path: 'resources/:slug', element: page(() => import('@/docs/ResourcesPage')) },
              { path: '*', element: <RouteError /> },
            ],
          },
        ],
      },
      { path: '*', element: <RouteError /> },
    ],
  },
])
